import {
	data,
	type HeadersFunction,
	Links,
	type LinksFunction,
	type LoaderFunctionArgs,
	Meta,
	type MetaFunction,
	Outlet,
	Scripts,
	ScrollRestoration,
	useLoaderData,
} from 'react-router'
import { HoneypotProvider } from 'remix-utils/honeypot/react'
import { BUSINESS_NAME } from '#app/utils/business.js'
import { GeneralErrorBoundary } from './components/errors/error-boundary.tsx'
import { EpicProgress } from './components/progress-bar.tsx'
import { useToast } from './components/toaster.tsx'
import { href as iconsHref } from './components/ui/icon.tsx'
import { EpicToaster } from './components/ui/sonner.tsx'
import fontStyles from './styles/font.css?url'
import sonnerCSS from './styles/sonner.css?url'
import tailwindStyleSheetUrl from './styles/tailwind.css?url'
import { getUserId, logout } from './utils/auth.server.ts'
import { ClientHintCheck, getHints } from './utils/client-hints.tsx'
import { prisma } from './utils/db.server.ts'
import { getEnv } from './utils/env.server.ts'
import { honeypot } from './utils/honeypot.server.ts'
import { i18n, useChangeLanguage } from './utils/i18n.tsx'
import { i18next } from './utils/i18next.server.ts'
import { combineHeaders, getDomainUrl } from './utils/misc.tsx'
import { useNonce } from './utils/nonce-provider.ts'
import { checkRedirections } from './utils/redirects.server.ts'
import { getSplashSeen } from './utils/splash-cookie.server.ts'
import { makeTimings, time } from './utils/timing.server.ts'
import { getToast } from './utils/toast.server.ts'

export const links: LinksFunction = () => {
	return [
		// Preload svg sprite as a resource to avoid render blocking
		{ rel: 'preload', href: iconsHref, as: 'image' },
		// Preload splash screen videos
		{
			rel: 'preload',
			href: '/videos/splash/vid-isotipo-ss-vertical.mp4',
			as: 'video',
			type: 'video/mp4',
		},
		{
			rel: 'preload',
			href: '/videos/splash/vid-isotipo-ss-horizontal.mp4',
			as: 'video',
			type: 'video/mp4',
		},
		// Preconnect to Adobe Typekit for faster font loading
		{ rel: 'dns-prefetch', href: 'https://use.typekit.net' },
		{
			rel: 'preconnect',
			href: 'https://use.typekit.net',
			crossOrigin: 'anonymous',
		} as const,
		// Preload the Typekit CSS file
		{
			rel: 'preload',
			href: 'https://use.typekit.net/rir7nzh.css',
			as: 'style',
		},
		// Preconnect to Google Tag Manager
		{ rel: 'dns-prefetch', href: 'https://www.googletagmanager.com' },
		{
			rel: 'preconnect',
			href: 'https://www.googletagmanager.com',
			crossOrigin: 'anonymous',
		} as const,
		// Preconnect to analytics
		{ rel: 'dns-prefetch', href: 'https://analytics.konam.dev' },
		{
			rel: 'preconnect',
			href: 'https://analytics.konam.dev',
			crossOrigin: 'anonymous',
		} as const,
		{
			rel: 'alternate icon',
			type: 'image/png',
			href: '/favicons/favicon-96x96.png',
		},
		{ rel: 'apple-touch-icon', href: '/favicons/apple-touch-icon.png' },
		{
			rel: 'manifest',
			href: '/site.webmanifest',
			crossOrigin: 'use-credentials',
		} as const, // necessary to make typescript happy
		{ rel: 'icon', type: 'image/svg+xml', href: '/favicons/favicon.svg' },
		{ rel: 'stylesheet', href: tailwindStyleSheetUrl },
		{
			rel: 'stylesheet',
			href: 'https://use.typekit.net/rir7nzh.css',
		},
		{ rel: 'stylesheet', href: fontStyles },
		{ rel: 'stylesheet', href: sonnerCSS },
	].filter(Boolean)
}

export const meta: MetaFunction<typeof loader> = ({ data }) => {
	return [
		{ title: data ? BUSINESS_NAME : `Error | ${BUSINESS_NAME}` },
		{ name: 'description', content: `Your own captain's log` },
	]
}

export async function loader({ request }: LoaderFunctionArgs) {
	const timings = makeTimings('root loader')

	const locale = await i18next.getLocale(request)

	await time(() => checkRedirections(request), {
		timings,
		type: 'checkRedirections',
		desc: 'checkRedirections in root',
	})

	const userId = await time(() => getUserId(request), {
		timings,
		type: 'getUserId',
		desc: 'getUserId in root',
	})

	const user = userId
		? await time(
				() =>
					prisma.user.findUniqueOrThrow({
						select: {
							id: true,
							name: true,
							username: true,
							image: { select: { id: true } },
							roles: {
								select: {
									name: true,
									permissions: {
										select: { entity: true, action: true, access: true },
									},
								},
							},
						},
						where: { id: userId },
					}),
				{ timings, type: 'find user', desc: 'find user in root' },
			)
		: null
	if (userId && !user) {
		console.info('something weird happened')
		// something weird happened... The user is authenticated but we can't find
		// them in the database. Maybe they were deleted? Let's log them out.
		await logout({ request, redirectTo: '/' })
	}
	const { toast, headers: toastHeaders } = await getToast(request)
	const honeyProps = honeypot.getInputProps()
	const splashSeen = getSplashSeen(request)

	return data(
		{
			user,
			requestInfo: {
				hints: getHints(request),
				origin: getDomainUrl(request),
				path: new URL(request.url).pathname,
				locale,
			},
			ENV: getEnv(),
			toast,
			honeyProps,
			splashSeen,
		},
		{
			headers: combineHeaders(
				{ 'Server-Timing': timings.toString() },
				toastHeaders,
			),
		},
	)
}

export const handle = {
	// In the handle export, we can add a i18n key with namespaces our route
	// will need to load. This key can be a single string or an array of strings.
	// TIP: In most cases, you should set this to your defaultNS from your i18n config
	// or if you did not set one, set it to the i18next default namespace "translation"
	i18n: 'common',
}

export const headers: HeadersFunction = ({ loaderHeaders }) => {
	const headers = {
		'Server-Timing': loaderHeaders.get('Server-Timing') ?? '',
	}
	return headers
}

function Document({
	children,
	origin,
	nonce,
	locale = i18n.fallbackLng,
	env = {},
	allowIndexing = true,
}: {
	children: React.ReactNode
	origin?: string
	nonce: string
	locale?: string
	env?: Record<string, string | undefined>
	allowIndexing?: boolean
}) {
	return (
		<html lang={locale} className={`h-full`}>
			<head>
				<ClientHintCheck nonce={nonce} />
				<Meta />
				<meta charSet="utf-8" />
				<meta name="viewport" content="width=device-width,initial-scale=1" />
				{env.MODE === 'production' &&
					origin === 'http://www.sapienstone.com' && (
						<script
							defer
							src="https://analytics.konam.dev/script.js"
							data-website-id="7504c469-3630-46f7-9075-0ac1b2123b5b"
							nonce={nonce}
						></script>
					)}
				{allowIndexing ? null : (
					<meta name="robots" content="noindex, nofollow" />
				)}
				<Links />
			</head>
			<body className="overflow-x-hidden bg-background text-foreground">
				{children}
				<script
					nonce={nonce}
					dangerouslySetInnerHTML={{
						__html: `window.ENV = ${JSON.stringify(env)}`,
					}}
				/>
				<ScrollRestoration nonce={nonce} />
				<Scripts nonce={nonce} />
			</body>
		</html>
	)
}

function App() {
	const data = useLoaderData<typeof loader>()
	const nonce = useNonce()
	const allowIndexing = data.ENV.ALLOW_INDEXING !== 'false'
	const { locale, origin } = data.requestInfo
	useToast(data.toast)
	useChangeLanguage(locale)

	return (
		<Document
			origin={origin}
			locale={locale}
			nonce={nonce}
			allowIndexing={allowIndexing}
			env={data.ENV}
		>
			<Outlet />
			<EpicToaster closeButton position="top-center" />
			<EpicProgress />
		</Document>
	)
}

function AppWithProviders() {
	const data = useLoaderData<typeof loader>()
	return (
		<HoneypotProvider {...data.honeyProps}>
			<App />
		</HoneypotProvider>
	)
}

export default AppWithProviders

export function ErrorBoundary() {
	// the nonce doesn't rely on the loader so we can access that
	const nonce = useNonce()

	// NOTE: you cannot use useLoaderData in an ErrorBoundary because the loader
	// likely failed to run so we have to do the best we can.
	// We could probably do better than this (it's possible the loader did run).
	// This would require a change in Remix.

	// Just make sure your root route never errors out and you'll always be able
	// to give the user a better UX.

	return (
		<Document nonce={nonce}>
			<GeneralErrorBoundary />
		</Document>
	)
}
